import { useState } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";
import { Form } from "react-final-form";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { makeStyles } from "@mui/styles";

import CloseIncidentForm from "../components/CloseIncidentForm";
import INCIDENT_FRAGMENT from "../fragments/incident";
import { QUERY as INCIDENT_QUERY } from "../containers/Incident";

const CLOSE_INCIDENT = gql`
  mutation closeIncident($input: CloseIncidentInput!) {
    incidents {
      close(input: $input) {
        incident {
          ...incident
        }
      }
    }
  }
  ${INCIDENT_FRAGMENT}
`;

const MARK_AS_CLAIM_FILED = gql`
  mutation markAsClaimFiled($input: MarkIncidentAsClaimFiledInput!) {
    incidents {
      markAsClaimFiled(input: $input) {
        incident {
          ...incident
        }
      }
    }
  }
  ${INCIDENT_FRAGMENT}
`;

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

const CloseIncidentDialog = ({
  incidentId,
  open,
  handleClose,
  onIncidentClosed,
}) => {
  const classes = useStyles();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [errorMessage, setErrorMessage] = useState(null);
  const [claimFiled, setClaimFiled] = useState(false);

  const [closeMutation] = useMutation(CLOSE_INCIDENT);
  const [markAsClaimFiledMutation] = useMutation(MARK_AS_CLAIM_FILED);

  const handleSubmit = async (form) => {
    try {
      setErrorMessage(null);

      if (claimFiled) {
        await markAsClaimFiledMutation({
          variables: {
            input: { incidentId },
          },
        });
      } else {
        await closeMutation({
          variables: { input: { incidentId, ...form } },
          update: (store) => {
            const data = store.readQuery({
              query: INCIDENT_QUERY,
              variables: { id: incidentId },
            });

            const newData = {
              ...data,
              node: {
                ...data.node,
                status: "CLOSED",
                closedReason: form.reason,
              },
            };

            store.writeQuery({
              query: INCIDENT_QUERY,
              variables: { id: incidentId },
              data: newData,
            });
          },
        });
      }

      onIncidentClosed();
    } catch (err) {
      setErrorMessage(err.message);
      console.log("Error submitting form", err);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      render={({ handleSubmit, submitting }) => (
        <Dialog open={open} fullWidth fullScreen={fullScreen}>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            Close incident
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <DialogContentText>
              Please give a reason for closing this incident. Once closed, the
              incident will no longer be editable.
            </DialogContentText>

            <CloseIncidentForm />

            <FormControlLabel
              control={
                <Checkbox
                  checked={claimFiled}
                  onChange={(e) => setClaimFiled(e.target.checked)}
                />
              }
              label="Claim filed"
            />

            {errorMessage && <p className={classes.error}>{errorMessage}</p>}
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button disabled={submitting} onClick={handleSubmit}>
              Close incident
            </Button>
          </DialogActions>
        </Dialog>
      )}
    />
  );
};

CloseIncidentDialog.propTypes = {
  incidentId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onIncidentClosed: PropTypes.func.isRequired,
};

export default CloseIncidentDialog;
